import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import ErrorMessage from '../ui/ErrorMessage';
import mcqService from '../../services/mcqService';

/**
 * QuestionFormModal - Modal for creating/editing MCQ questions
 * Handles question text, 5 options (A/B/C/D/E), correct answer, and explanation
 */
const QuestionFormModal = ({ isOpen, onClose, onSave, question, mcqId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    question_text: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    option_e: '',
    correct_answer: 'A',
    explanation: ''
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // Initialize form data when editing
  useEffect(() => {
    if (question && isOpen) {
      setFormData({
        question_text: question.question_text || '',
        option_a: question.option_a || '',
        option_b: question.option_b || '',
        option_c: question.option_c || '',
        option_d: question.option_d || '',
        option_e: question.option_e || '',
        correct_answer: question.correct_answer || 'A',
        explanation: question.explanation || ''
      });
    }
  }, [question, isOpen]);

  const resetForm = () => {
    setFormData({
      question_text: '',
      option_a: '',
      option_b: '',
      option_c: '',
      option_d: '',
      option_e: '',
      correct_answer: 'A',
      explanation: ''
    });
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.question_text.trim()) {
      setError('Question text is required');
      return;
    }
    if (!formData.option_a.trim() || !formData.option_b.trim() ||
        !formData.option_c.trim() || !formData.option_d.trim() || !formData.option_e.trim()) {
      setError('All 5 options (A, B, C, D, E) are required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (question) {
        // Update existing question
        await mcqService.updateQuestion(question.id, formData);
      } else {
        // Create new question
        await mcqService.createQuestion({
          ...formData,
          mcq_id: mcqId
        });
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to save question:', err);
      setError(err.response?.data?.detail || 'Failed to save question. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={question ? 'Edit Question' : 'Create New Question'}
      maxWidth="4xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <ErrorMessage message={error} onClose={() => setError('')} />
        )}

        {/* Question Text */}
        <div>
          <label htmlFor="question_text" className="block text-sm font-medium text-gray-700 mb-1">
            Question Text <span className="text-red-500">*</span>
          </label>
          <textarea
            id="question_text"
            name="question_text"
            value={formData.question_text}
            onChange={handleInputChange}
            rows="3"
            placeholder="Enter your question here..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        {/* Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Option A */}
          <div>
            <label htmlFor="option_a" className="block text-sm font-medium text-gray-700 mb-1">
              Option A <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="option_a"
              name="option_a"
              value={formData.option_a}
              onChange={handleInputChange}
              placeholder="Enter option A"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Option B */}
          <div>
            <label htmlFor="option_b" className="block text-sm font-medium text-gray-700 mb-1">
              Option B <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="option_b"
              name="option_b"
              value={formData.option_b}
              onChange={handleInputChange}
              placeholder="Enter option B"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Option C */}
          <div>
            <label htmlFor="option_c" className="block text-sm font-medium text-gray-700 mb-1">
              Option C <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="option_c"
              name="option_c"
              value={formData.option_c}
              onChange={handleInputChange}
              placeholder="Enter option C"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Option D */}
          <div>
            <label htmlFor="option_d" className="block text-sm font-medium text-gray-700 mb-1">
              Option D <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="option_d"
              name="option_d"
              value={formData.option_d}
              onChange={handleInputChange}
              placeholder="Enter option D"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Option E */}
          <div>
            <label htmlFor="option_e" className="block text-sm font-medium text-gray-700 mb-1">
              Option E <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="option_e"
              name="option_e"
              value={formData.option_e}
              onChange={handleInputChange}
              placeholder="Enter option E"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
        </div>

        {/* Correct Answer */}
        <div>
          <label htmlFor="correct_answer" className="block text-sm font-medium text-gray-700 mb-2">
            Correct Answer <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-5 gap-3">
            {['A', 'B', 'C', 'D', 'E'].map(option => (
              <button
                key={option}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, correct_answer: option }))}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  formData.correct_answer === option
                    ? 'bg-green-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Option {option}
                {formData.correct_answer === option && (
                  <svg className="w-5 h-5 inline-block ml-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Explanation */}
        <div>
          <label htmlFor="explanation" className="block text-sm font-medium text-gray-700 mb-1">
            Explanation (Optional)
          </label>
          <textarea
            id="explanation"
            name="explanation"
            value={formData.explanation}
            onChange={handleInputChange}
            rows="3"
            placeholder="Explanation shown to students after they submit (optional)"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-sm text-gray-500 mt-1">
            Questions are automatically numbered in the order they are created. Students see them in randomized order.
          </p>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? 'Saving...' : (question ? 'Update Question' : 'Create Question')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default QuestionFormModal;
